import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import { boundedNote } from "../board.ts";
import {
  ADD_REFUSAL,
  listingNote,
  PRODUCTS_SCOPE,
  productRow,
  stateOf,
  statesPresent,
  type Product,
  type ProductStatus,
} from "../products.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `menu` in `22 WP21 Merchant Portal.dc.html`: what customers can buy.
 *
 * **This screen was ready for seven cohorts and had nowhere to be drawn.**
 * `tests/contract/console-screen-support.test.ts` recorded it as the one of the forty-two that was
 * *ready here and not drawn*, with one reason: "whose channel has no application at all". COM-02's
 * listing has been published since the cohort that served the finance boards. This portal is the
 * application, and this is the screen that was waiting for it.
 *
 * What is drawn from data and what is not — `../products.ts` argues each:
 *
 * | drawn                            | source                                                    |
 * | -------------------------------- | --------------------------------------------------------- |
 * | the rows                         | COM-02 `GET /v1/tenants/{tenantId}/products`              |
 * | `Product` — `Avocado, Hass`      | `name`. **The only real name on this portal**              |
 * | `Category` — `Fruit and veg`     | `categoryId`. The name is inside COM-01's catalogue       |
 * | `Price` — `KES 60.00`            | `price`, with its currency and full minor units           |
 * | `each`, `bunch`                  | **nothing** — no unit is published                        |
 * | `KES 130.00 → 140.00`            | **nothing** — no future price, no effective date          |
 * | `caution · no photo`             | **nothing** — COM-02 publishes no photo field at all      |
 * | the four tiles                   | no listing publishes a total (R-04); no tile row here     |
 * | the primary command              | drawn and refused — `ADD_REFUSAL`                         |
 *
 * **The category absence is the one worth arguing about, because it is reachable.** COM-02 publishes
 * `categoryId` and `catalogId`; the name is a category entry inside the catalogue, and COM-01's
 * read answers it. So unlike most absences on this platform this one could be filled — by fetching
 * a catalogue per distinct `catalogId`, which is a second service on one screen and the exact defect
 * the dispatch console's live map was rebuilt to remove. The honest fix is one denormalised column:
 * COM-02 publishing `categoryName` beside `categoryId`. Recorded as the thing to ask for.
 *
 * **This board reads the tenant and not the merchant**, which is the one of the three that does.
 * COM-02's listing is addressed `/v1/tenants/{tenantId}/products`, so a merchant reading it is
 * answered the tenant's whole catalogue — every merchant's products, not only their own. That is a
 * tenancy question rather than a rendering one and it is recorded under the grid: the operation
 * publishes `merchantId` per row, so the rows can be told apart, and a merchant-scoped sibling is
 * what would make the board theirs. Filtering here would hide rows without the service agreeing to.
 */

const product = services.commerce__product;

export interface ProductsAndMenuProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `EAM › Shop › Products and menu`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Products and menu";
const SOURCE = "COM-02";
const DESCRIPTION = "What customers can buy, with prices, photos and options.";
const COLUMNS = ["Product", "Category", "Price"] as const;
const NOTE =
  "Price changes apply to new carts only; carts already open keep the old price for their hold time.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's own `FORMS.menu[0]`, which is the word on its heading button. */
const PRIMARY = "Add product";

/** The most rows one request answers, which is this listing's own maximum. */
const PER_FETCH = 200;

/** COM-02's published statuses, so a value outside them is read as the safe one. */
const STATUSES: readonly ProductStatus[] = ["draft", "active", "suspended", "retired"];

/**
 * Narrow one published row onto the shape this board reads.
 *
 * A status the contract's enum does not cover is read as `suspended`, which is the safe direction:
 * `suspended` means customers cannot see it, and a product whose status this console cannot read
 * must not be drawn as live — a merchant would believe it was selling.
 */
function productOf(row: {
  productId: string;
  tenantId: string;
  merchantId: string;
  catalogId: string;
  categoryId: string;
  sku: string;
  name: string;
  description: string;
  price: { amountMinor: number; currency: string };
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}): Product {
  return {
    productId: row.productId,
    tenantId: row.tenantId,
    merchantId: row.merchantId,
    catalogId: row.catalogId,
    categoryId: row.categoryId,
    sku: row.sku,
    name: row.name,
    description: row.description,
    price: row.price,
    status: STATUSES.find((candidate) => candidate === row.status) ?? "suspended",
    version: row.version,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

export function ProductsAndMenu({ transport, session, crumbs }: ProductsAndMenuProps): ReactNode {
  const [rows, setRows] = useState<readonly Product[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    if (!session.scopes.has(PRODUCTS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [PRODUCTS_SCOPE] });
      return;
    }

    /*
     * The tenant, not the merchant — and no second call for the category names.
     *
     * COM-01's catalogue read would answer them, one request per distinct catalogue, and that is
     * the fan-out Document 17 WEB-0743 tolerates for one listing per screen and not for a column.
     * The cell draws the identifier and the footnote says what would fix it.
     */
    try {
      /*
       * `limit` is the **sixth** positional argument, after four optional filters:
       * `listProducts(tenantId, catalogId?, categoryId?, merchantId?, status?, limit?, after?)`.
       *
       * It was passed second, so this was not a request for two hundred products — it was a
       * request for the products of a **catalogue whose identifier is the string `"200"`**. No
       * catalogue has that identifier, so COM-02 answered an empty page, every time, and this
       * board could never draw a product. Measured 26 October against ten active products it
       * should have shown.
       *
       * Nothing failed: the call was well formed, the response was a valid empty page, and the
       * board drew its designed empty state. A read that silently matches nothing is the quietest
       * bug this codebase has, and it has now appeared four times in one day.
       */
      const page = await product
        .createProductClient(transport)
        .listProducts(
          session.tenantId,
          undefined,
          undefined,
          undefined,
          undefined,
          String(PER_FETCH),
        );
      setRows(page.products.map(productOf));
      setTruncated(typeof page.nextCursor === "string");
    } catch (error) {
      setFailure(stateForError(error, [PRODUCTS_SCOPE]));
    }
  }, [transport, session.scopes, session.tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((row) => state === "" || stateOf(row).label === state).map(productRow);

  const bounded = boundedNote(rows?.length, truncated, "products", "newest first");
  const footnote = listingNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Nothing is on the menu yet. A row appears here once a product is created with COM-02."
      primary={{ label: PRIMARY, refusal: ADD_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="products-and-menu"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
